import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    useGetLink, useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showTopContent = "showTopContent",
    topContent = "topContent",
    showLineAfterPhotograph = "showLineAfterPhotograph", 
    directContactLabelText = "directContactLabelText",
    extensionLabelText = "extensionLabelText",
    cellPhoneLabelText = "cellPhoneLabelText",
    showBottomContent = "showBottomContent",
    bottomContent = "bottomContent",
    bioCardsData = "bioCardsData",
}

interface BioCardItem {
    fields: {
        photo: string;
        photoAltText: string;
        name: string;
        role: string;
        locationName: string;
        directContactNumber: string;
        directContactExtensionCode: string;
        cellPhoneNumber: string;
        emailAddress: string;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTopContent]: boolean;
        [fields.topContent]: string;
        [fields.showLineAfterPhotograph]: boolean;
        [fields.directContactLabelText]: string;
        [fields.extensionLabelText]: string;
        [fields.cellPhoneLabelText]: string;
        [fields.bioCardsData]: BioCardItem[];
        [fields.showBottomContent]: boolean;
        [fields.bottomContent]: string;
    };
}

export interface DsgBioCardsStyles {
    mainWrapper?: InjectableCss;
    bioCardsWrapper?: InjectableCss;
    bioCardItem?: InjectableCss;
    imageWrapper?: InjectableCss;
    dividerLine?: InjectableCss;
    bioCardTextDetails?: InjectableCss;
    topContentWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    nameText?: TypographyPresentationProps;
    roleText?: TypographyPresentationProps;
    locationName?: TypographyPresentationProps;
    contactNumber?: InjectableCss;
    emailAddress?: InjectableCss;
}

export const dsgBioCardsStyles: DsgBioCardsStyles = {
    mainWrapper: {
        css: css`
            padding: 24px 0;
            max-width: 1062px;
            margin: auto;
            display: flex;
            flex-direction: column;
            gap: 24px;
        `
    },
    bioCardsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 16px 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    gap: 24px;
                `,
                css`
                    gap: 40px;
                `,
            ], "min")}
        `
    },
    bioCardItem: {
        css: css`
            padding: 16px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 16px;
            background: ${colorPallete.white};
            box-shadow: ${elevation.dp04With25};
            width: calc(100%/2 - 12px);
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    width: calc(100%/3 - 16px);
                `,
                css`
                    width: calc(100%/4 - 30px);
                `,
            ], "min")}
        `
    },
    bioCardTextDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
        `
    },
    dividerLine: {
        css: css`
            border-top: 1px solid ${colorPallete.grey600DisabledText};
            max-width: 100px;
            width: 100%;
        `
    },
    imageWrapper: {
        css: css`
            display: flex;
            img {
                max-height: 200px;
                width: 100%;
                height: auto;
            }
        `
    },
    nameText: {
        size: bodyFonts.body2SemiBoldLg.fontSize,
        weight: bodyFonts.body2SemiBoldLg.fontWeight,
        lineHeight: bodyFonts.body2SemiBoldLg.lineHeight,
    },
    roleText: {
        size: bodyFonts.body2RegularLg.fontSize,
        weight: bodyFonts.body2RegularLg.fontWeight,
        lineHeight: bodyFonts.body2RegularLg.lineHeight,
    },
    locationName: {
        size: labelFonts.label1Regular.fontSize,
        weight: labelFonts.label1Regular.fontWeight,
        lineHeight: labelFonts.label1Regular.lineHeight,
    },
    contactNumber: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            span {
                font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            }
        `
    },
    emailAddress: {
        css: css`
            display: flex;
            span {
                font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            }
        `
    },
};

const styles = dsgBioCardsStyles;

export const DsgBioCards: FC<OwnProps> = ({ fields }) => {

    const { bioCardsData } = fields;

    return (
        <StyledWrapper {...styles.mainWrapper} className="bioCardDetails"> 
            {fields.showTopContent && (
                <StyledWrapper {...styles.topContentWrapper}>
                    {parse(fields.topContent, parserOptions)}
                </StyledWrapper>
            )}
            {bioCardsData.length > 0 && (
                <StyledWrapper {...styles.bioCardsWrapper}>
                    {bioCardsData.map((item, index) => (
                        <StyledWrapper {...styles.bioCardItem} key={`bioCards-${index}`}>
                            {item.fields.photo && (
                                <StyledWrapper {...styles.imageWrapper}>
                                    <Img src={item.fields.photo} altText={item.fields.photoAltText || ''} />  
                                </StyledWrapper>
                            )}
                            {fields.showLineAfterPhotograph && (
                                <StyledWrapper {...styles.dividerLine}>
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.bioCardTextDetails}>
                                {item.fields.name && (
                                    <Typography {...styles.nameText}>
                                        {parse(item.fields.name, parserOptions)}
                                    </Typography>
                                )}
                                {item.fields.role && (
                                    <Typography {...styles.roleText}>
                                        {parse(item.fields.role, parserOptions)}
                                    </Typography>
                                )}
                                {item.fields.locationName && (
                                    <Typography {...styles.locationName}>
                                        {parse(item.fields.locationName, parserOptions)}
                                    </Typography>
                                )}
                                {item.fields.directContactNumber && (
                                    <StyledWrapper {...styles.contactNumber}>
                                        {fields.directContactLabelText && (
                                            <Typography>
                                                {fields.directContactLabelText}&nbsp;
                                            </Typography>
                                        )}
                                        <Link href={`tel:${item.fields.directContactNumber}`} className="text-link-secondary text-link-active">
                                            <Typography>
                                                {parse(item.fields.directContactNumber, parserOptions)}
                                              
                                                {item.fields.directContactExtensionCode && (
                                                    <>
                                                        {fields.extensionLabelText && (
                                                            <>
                                                                &nbsp;{fields.extensionLabelText}
                                                            </>
                                                        )}
                                                        {item.fields.directContactExtensionCode}
                                                    </>
                                                )}
                                            </Typography>
                                            
                                        </Link>
                                    
                                    </StyledWrapper>
                                )}
                                {item.fields.cellPhoneNumber && (
                                    <StyledWrapper {...styles.contactNumber}>
                                        {fields.cellPhoneLabelText && (
                                            <Typography>
                                                {fields.cellPhoneLabelText}&nbsp;
                                            </Typography>
                                        )}
                                        <Link href={`tel:${item.fields.cellPhoneNumber}`} className="text-link-secondary text-link-active">
                                            <Typography>
                                                {parse(item.fields.cellPhoneNumber, parserOptions)}
                                            </Typography>
                                        </Link>
                                    </StyledWrapper>
                                )}
                                {item.fields.emailAddress && (
                                    <StyledWrapper {...styles.emailAddress}>
                                        <Link href={`mailto:${item.fields.emailAddress}`} className="text-link-secondary text-link-active">
                                            <Typography>
                                                {parse(item.fields.emailAddress, parserOptions)}
                                            </Typography>
                                        </Link>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            )}
            {fields.showBottomContent && (
                <StyledWrapper {...styles.bottomContentWrapper}>
                    {parse(fields.bottomContent, parserOptions)}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: DsgBioCards,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.showTopContent,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                sortOrder: 0,
                fieldType: "General",
            },
            {
                name: fields.topContent,
                displayName: "Top Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 1,
                isVisible: widget => widget.fields.showTopContent,
            },
            {
                name: fields.showLineAfterPhotograph,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                sortOrder: 2,
                fieldType: "General",
            },
            {
                name: fields.directContactLabelText,
                editorTemplate: "TextField",
                displayName: "Direct Contact Label Text",
                defaultValue: "Direct",
                fieldType: "Contextual",
                sortOrder: 3,
            },
             {
                name: fields.extensionLabelText,
                editorTemplate: "TextField",
                displayName: "Extension Label Text",
                defaultValue: "ext:",
                fieldType: "Contextual",
                sortOrder: 4,
            }, 
            {
                name: fields.cellPhoneLabelText,
                editorTemplate: "TextField",
                displayName: "Cell Phone Label Text",
                defaultValue: "Cell",
                fieldType: "Contextual",
                sortOrder: 5,
            },
            {
                name: fields.bioCardsData,
                editorTemplate: "ListField",
                displayName: "Bio Cards",
                getDisplay: (item: HasFields) => {
                    const {
                        name,
                    } = item.fields;
                    if (name) {
                        return name;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 6,
                fieldDefinitions: [
                    {
                        name: "photo",
                        displayName: 'Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        displayName: "Full Name",
                        defaultValue: "",
                        sortOrder: 2,
                        isRequired: true,
                    },
                    {
                        name: "photoAltText",
                        editorTemplate: "TextField",
                        displayName: "Photo Image Alt Text",
                        defaultValue: "",
                        sortOrder: 2,
                    },
                    {
                        name: "role",
                        editorTemplate: "TextField",
                        displayName: "Role Name",
                        defaultValue: "",
                        sortOrder: 3,
                    },
                    {
                        name: "locationName",
                        editorTemplate: "TextField",
                        displayName: "Location Name",
                        defaultValue: "",
                        sortOrder: 4,
                    },
                    {
                        name: "directContactNumber",
                        editorTemplate: "TextField",
                        displayName: "Direct Contact Number",
                        defaultValue: "",
                        sortOrder: 5,
                    },
                    {
                        name: "directContactExtensionCode",
                        editorTemplate: "TextField",
                        displayName: "Direct Contact Extension Code",
                        defaultValue: "",
                        sortOrder: 6,
                    },
                    {
                        name: "cellPhoneNumber",
                        editorTemplate: "TextField",
                        displayName: "Cell Phone Number",
                        defaultValue: "",
                        sortOrder: 7,
                    },
                    {
                        name: "emailAddress",
                        editorTemplate: "TextField",
                        displayName: "Email Address",
                        defaultValue: "",
                        sortOrder: 8,
                    },
                ],
            },
            {
                name: fields.showBottomContent,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                sortOrder: 7,
                fieldType: "General",
            },
            {
                name: fields.bottomContent,
                displayName: "Bottom Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 8,
                isVisible: widget => widget.fields.showBottomContent,
            },
        ],
    },
};

export default widgetModule;